/**
 * rosterMoveGuards — Layer 1 of the 4-layer promotion-modal invariant
 * (Tony 2026-05-13 structural directive).
 *
 * INVARIANT: the promotion-cost modal must NEVER appear for a player with
 * an existing contract. Implemented at four layers (each independently
 * sufficient):
 *
 *   Layer 1 — this file (frontend guard)
 *   Layer 2 — backend gate at roster_service.py move_player_roster_status
 *   Layer 3 — RosterPlanExecutor minor_promotion composer
 *   Layer 4 — PromotionConfirmModal render-path defense-in-depth
 *
 * SHAPE DISCIPLINE — READ CANONICAL FIRST
 *
 * The canonical player shape emits financial via the canonical SELECT
 * at player_repository.py:2973-2982:
 *   league_context.financial = {
 *     contract_salary,         <-- canonical key (numeric)
 *     contract_years,
 *     contract_type,
 *     baseline_price,
 *     current_market_value
 *   }
 *
 * The raw JSONB storage shape (read directly by backend services) is:
 *   roster_data.financial = { salary, contract_years, contract_type, ... }
 *
 * Frontend MUST read the canonical key `contract_salary`. The legacy
 * `salary` field is kept as a defensive fallback for any not-yet-migrated
 * read path, but in production every player-modal / roster / FA endpoint
 * returns the canonical shape.
 *
 * HISTORICAL BUG (this file caused it; 2026-05-13):
 *
 * The 9f.3 implementation of isPriceLessPlayer read `financial.salary` and
 * claimed in its docstring to "mirror the backend's existing_salary == 0
 * check at services/roster_service.py". That mirror was BROKEN: backend
 * reads `salary` from RAW JSONB; frontend was reading `salary` from the
 * CANONICAL shape where the key is `contract_salary`. The frontend's
 * `salary` lookup always returned undefined, so the guard ALWAYS treated
 * canonical-shape players as price-less and ALWAYS fired the modal for
 * minors-exit moves. This is the Christian Scott misquote bug.
 *
 * Fix per Tony's invariant: read `contract_salary` first; treat the
 * player as contract-bearing if EITHER `contract_salary > 0` OR
 * `contract_type` is a real contract type (i.e., not NULL, not the
 * canonical ML-draft '0' flag per Bible § 4.5.3).
 */

const MINORS_EXIT_DESTINATIONS = new Set(['active', 'bench']);

/**
 * Does the player LACK a real contract?
 *
 * Returns true iff the player has NO contract-bearing signal at all.
 * A real contract is signaled by EITHER:
 *   - canonical contract_salary > 0 (read first), OR
 *   - contract_type is a real contract type (not null/empty, not '0')
 *     — per Bible § 4.5.3, contract_type='0' is the canonical ML-draft
 *     "no contract yet" marker.
 *
 * Falls back to the legacy JSONB-write shape (`roster_data.financial.salary`)
 * for any caller that has not yet been migrated to canonical reads.
 * Production endpoints emit canonical, so the fallback path is dead in
 * production but kept for defense.
 *
 * The promotion-confirmation modal must NEVER appear for a player with
 * an existing contract. This function plus Layer 4 (modal render-path
 * re-check) plus Layer 2/3 (backend gate + executor) together enforce it.
 */
export const isPriceLessPlayer = (player) => {
  const financial =
    player?.league_context?.financial ??
    player?.roster_data?.financial ??
    {};

  // Canonical key first; legacy fallback for any non-canonical caller.
  const salaryRaw = financial.contract_salary ?? financial.salary;
  const hasRealSalary = salaryRaw != null && Number(salaryRaw) > 0;

  const contractType = financial.contract_type;
  const hasRealContractType =
    contractType != null && contractType !== '0' && contractType !== '';

  // EITHER signal is sufficient — player is contract-bearing.
  return !(hasRealSalary || hasRealContractType);
};

/**
 * Should the UI show a promotion-confirmation modal before this move?
 *
 * Returns true iff:
 *   - fromStatus === 'minors'
 *   - toStatus ∈ {'active', 'bench'}
 *   - player has no contract (isPriceLessPlayer)
 *
 * Inverse cases (NO confirmation) — exhaustive:
 *   - any non-minors origin (bench→active, active→bench, etc.) — no charge in those paths
 *   - minors→{minors,dl} — backend doesn't charge on those transitions either
 *   - minors→{active,bench} for a player WITH contract — re-promotion, no charge
 */
export const requiresPromotionConfirmation = ({ player, fromStatus, toStatus }) => {
  if (fromStatus !== 'minors') return false;
  if (!MINORS_EXIT_DESTINATIONS.has(toStatus)) return false;
  return isPriceLessPlayer(player);
};

/**
 * Compute the projected cost and contract terms for the confirmation modal.
 *
 * Reads from league.salary_settings.{promotion_cost, standard_contract_length}.
 * Defaults mirror the backend defaults in promote_from_minors() but per
 * Bible Rule 9 these are display fallbacks only — backend is authoritative.
 *
 * Per Tony's C1 + 9e audit: price-null = ML draft regardless of stored
 * acquisition_method, so we use promotion_cost (the ML-draft rate), not
 * rookie_price.
 */
export const computePromotionPreview = ({ league }) => {
  const settings = league?.salary_settings ?? {};
  const promotionCost = settings.promotion_cost ?? 10;
  const contractYears = settings.standard_contract_length ?? 2;
  return {
    cost: Number(promotionCost),
    contractYears: Number(contractYears),
  };
};
